const instances = new Set();
const PROTECTION_RATE_BASIS_POINTS = 200;

const coordinator = {
  syncing: null
};

function cartRoot() {
  return window.Shopify?.routes?.root || "/";
}

function getStorageValue() {
  try {
    return window.sessionStorage.getItem("meepoShippingProtection");
  } catch (error) {
    return null;
  }
}

function setStorageValue(value) {
  try {
    window.sessionStorage.setItem("meepoShippingProtection", value ? "1" : "0");
  } catch (error) {}
}

function formatMoney(cents) {
  const settings = window.themeVariables?.settings || {};
  const format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;

  if (!format) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholderRegex);

  if (!match) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
    if (Number.isNaN(number) || number == null) {
      return "0";
    }

    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split(".");
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : "";

    return dollars + centsPart;
  }

  let value = "";

  switch (match[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }

  return format.replace(placeholderRegex, value);
}

function parseConfig(element) {
  let tiers = [];

  try {
    tiers = JSON.parse(element.dataset.tiers || "[]");
  } catch (error) {
    tiers = [];
  }

  tiers = tiers
    .map((tier) => ({
      price: Number(tier.price),
      variantId: Number(tier.variantId)
    }))
    .filter((tier) => tier.price > 0 && tier.variantId > 0)
    .sort((a, b) => a.price - b.price);

  return {
    configured: element.dataset.configured === "true" && tiers.length > 0,
    cleanupOnly: element.dataset.cleanupOnly === "true",
    defaultSelected: element.dataset.defaultSelected === "true",
    previewPriceCents: Number(element.dataset.previewPriceCents || 1399),
    productId: Number(element.dataset.productId || 0),
    tiers
  };
}

async function fetchCart() {
  const response = await fetch(`${cartRoot()}cart.js`, {
    headers: {
      Accept: "application/json"
    }
  });

  return response.json();
}

function getBundledSections() {
  const sections = [];

  document.documentElement.dispatchEvent(
    new CustomEvent("cart:prepare-bundled-sections", {
      bubbles: true,
      detail: { sections }
    })
  );

  return [...new Set(sections.filter(Boolean))];
}

async function fetchSections() {
  const sections = getBundledSections();

  if (sections.length === 0) {
    return {};
  }

  const params = new URLSearchParams({ sections: sections.join(",") });
  const response = await fetch(`${cartRoot()}?${params.toString()}`);

  return response.json();
}

function isProtectionItem(item, config) {
  const protectionVariantIds = new Set(config.tiers.map((tier) => tier.variantId));

  return (
    protectionVariantIds.has(Number(item.variant_id)) ||
    Number(item.product_id) === config.productId ||
    item.properties?._meepo_shipping_protection === "true"
  );
}

function getProtectionItems(cart, config) {
  return cart.items.filter((item) => isProtectionItem(item, config));
}

function getEligibleSubtotal(cart, config) {
  const itemsSubtotal = cart.items
    .filter((item) => item.requires_shipping && !isProtectionItem(item, config))
    .reduce((sum, item) => sum + item.final_line_price, 0);

  const cartLevelDiscount = (cart.cart_level_discount_applications || []).reduce(
    (sum, discount) => sum + (discount.total_allocated_amount || 0),
    0
  );

  return Math.max(0, itemsSubtotal - cartLevelDiscount);
}

function getTargetProtectionPrice(subtotal) {
  return Math.ceil((subtotal * PROTECTION_RATE_BASIS_POINTS) / 10000);
}

function getTierForSubtotal(subtotal, config) {
  const targetPrice = getTargetProtectionPrice(subtotal);

  return config.tiers.find((tier) => tier.price >= targetPrice) || config.tiers[config.tiers.length - 1];
}

function getDesiredState(cart, config) {
  const protectionItems = getProtectionItems(cart, config);
  const subtotal = getEligibleSubtotal(cart, config);
  const storedValue = getStorageValue();
  const hasProtection = protectionItems.length > 0;
  const shouldSelectByDefault = storedValue === null && config.defaultSelected;
  const shouldSelectByStorage = storedValue === "1";

  return {
    subtotal,
    protectionItems,
    hasProtection,
    selected: subtotal > 0 && (hasProtection || shouldSelectByStorage || shouldSelectByDefault),
    tier: subtotal > 0 ? getTierForSubtotal(subtotal, config) : null
  };
}

async function updateLines(updates) {
  const response = await fetch(`${cartRoot()}cart/update.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({ updates })
  });

  if (!response.ok) {
    throw new Error("Unable to update cart lines.");
  }

  return response.json();
}

async function addProtectionVariant(variantId) {
  const response = await fetch(`${cartRoot()}cart/add.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Meepo-Shipping-Protection": "true"
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            _meepo_shipping_protection: "true"
          }
        }
      ]
    })
  });

  if (!response.ok) {
    throw new Error("Unable to add Shipping Protection.");
  }

  return response.json();
}

async function refreshCartUi(baseEvent) {
  const [cart, sections] = await Promise.all([fetchCart(), fetchSections()]);

  cart.sections = sections;
  updateInstances(cart);

  if (Object.keys(sections).length > 0) {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent,
          cart
        }
      })
    );
  } else {
    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
  }

  return cart;
}

async function syncProtection(desiredSelected, config, cart = null) {
  if (desiredSelected && !config.configured) {
    return cart;
  }

  if (coordinator.syncing) {
    return coordinator.syncing;
  }

  coordinator.syncing = (async () => {
    let workingCart = cart || (await fetchCart());
    let state = getDesiredState(workingCart, config);
    let shouldSelect = desiredSelected && state.subtotal > 0;
    let mutated = false;

    if (state.subtotal === 0) {
      shouldSelect = false;
    }

    if (!shouldSelect) {
      if (state.protectionItems.length > 0) {
        await updateLines(Object.fromEntries(state.protectionItems.map((item) => [item.key, 0])));
        mutated = true;
      }
    } else if (state.tier) {
      const desiredVariantId = state.tier.variantId;
      const matchingItems = state.protectionItems.filter((item) => Number(item.variant_id) === desiredVariantId);
      const primaryItem = matchingItems[0];
      const updates = {};

      for (const item of state.protectionItems) {
        if (item !== primaryItem) {
          updates[item.key] = 0;
        }
      }

      if (primaryItem && primaryItem.quantity !== 1) {
        updates[primaryItem.key] = 1;
      }

      if (Object.keys(updates).length > 0) {
        await updateLines(updates);
        mutated = true;
      }

      if (!primaryItem) {
        await addProtectionVariant(desiredVariantId);
        mutated = true;
      }
    }

    if (mutated) {
      if (window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
        return null;
      }

      return refreshCartUi("shipping-protection:change");
    }

    updateInstances(workingCart);
    return workingCart;
  })();

  try {
    return await coordinator.syncing;
  } finally {
    coordinator.syncing = null;
  }
}

function updateInstances(cart) {
  instances.forEach((instance) => instance.render(cart));
}

class ShippingProtection extends HTMLElement {
  connectedCallback() {
    if (this.connected) {
      return;
    }

    this.connected = true;
    this.config = parseConfig(this);
    this.checkbox = this.querySelector("[data-shipping-protection-toggle]");
    this.priceElement = this.querySelector("[data-shipping-protection-price]");
    this.statusElement = this.querySelector("[data-shipping-protection-status]");
    this.onCheckboxChange = this.handleCheckboxChange.bind(this);
    this.onCartChange = this.handleCartChange.bind(this);

    instances.add(this);

    if (this.config.cleanupOnly) {
      this.initializeCleanup();
      return;
    }

    if (!this.config.configured) {
      this.renderPreview();
      return;
    }

    this.checkbox?.addEventListener("change", this.onCheckboxChange);
    document.addEventListener("cart:change", this.onCartChange);

    this.initialize();
  }

  disconnectedCallback() {
    instances.delete(this);

    if (this.checkbox && this.onCheckboxChange) {
      this.checkbox.removeEventListener("change", this.onCheckboxChange);
    }

    if (this.onCartChange) {
      document.removeEventListener("cart:change", this.onCartChange);
    }
  }

  async initialize() {
    try {
      const cart = await fetchCart();
      const state = getDesiredState(cart, this.config);
      let currentCart = cart;

      if (state.hasProtection || state.selected) {
        currentCart = await syncProtection(state.selected, this.config, cart);

        if (currentCart === null) {
          return;
        }
      }

      this.render(currentCart);
      this.completeInitialization();
    } catch (error) {
      await this.recoverFromSyncFailure();
    }
  }

  async initializeCleanup() {
    try {
      const cart = await fetchCart();
      const currentCart = await syncProtection(false, this.config, cart);

      if (currentCart !== null) {
        this.completeInitialization();
      }
    } catch (error) {
      this.completeInitialization();
    }
  }

  completeInitialization() {
    this.removeAttribute("data-initializing");
    this.removeAttribute("aria-busy");
  }

  renderPreview() {
    this.hidden = false;

    if (this.priceElement) {
      this.priceElement.hidden = false;
      this.priceElement.innerHTML = formatMoney(this.config.previewPriceCents);
    }
  }

  render(cart) {
    if (this.config.cleanupOnly) {
      this.hidden = true;
      return;
    }

    if (!this.config.configured || !cart) {
      this.renderPreview();
      return;
    }

    const state = getDesiredState(cart, this.config);

    this.hidden = state.subtotal === 0 && state.protectionItems.length === 0;

    if (state.tier && this.priceElement) {
      this.priceElement.hidden = false;
      this.priceElement.innerHTML = formatMoney(state.tier.price);
    }

    if (this.checkbox) {
      this.checkbox.checked = state.selected;
      this.checkbox.disabled = this.hidden;
    }

    if (state.protectionItems.length > 0 && state.subtotal === 0) {
      syncProtection(false, this.config, cart).catch(() => this.recoverFromSyncFailure());
    }
  }

  handleCartChange(event) {
    const cart = event.detail?.cart;

    if (!cart || coordinator.syncing) {
      return;
    }

    this.render(cart);

    const state = getDesiredState(cart, this.config);

    if (state.hasProtection) {
      syncProtection(true, this.config, cart).catch(() => this.recoverFromSyncFailure());
    }
  }

  async handleCheckboxChange(event) {
    const selected = event.target.checked;

    this.setBusy(true);
    this.setStatus("");

    try {
      await syncProtection(selected, this.config);
      setStorageValue(selected);
    } catch (error) {
      await this.recoverFromSyncFailure();
    } finally {
      this.setBusy(false);
    }
  }

  async recoverFromSyncFailure() {
    setStorageValue(false);

    try {
      const cart = await fetchCart();
      const hasProtection = getProtectionItems(cart, this.config).length > 0;

      setStorageValue(hasProtection);
      this.render(cart);
    } catch (error) {
      this.hidden = false;

      if (this.priceElement) {
        this.priceElement.hidden = true;
      }

      if (this.checkbox) {
        this.checkbox.checked = false;
        this.checkbox.disabled = true;
      }
    }

    this.setStatus("Unable to update Shipping Protection. Please try again.");
    this.completeInitialization();
  }

  setBusy(isBusy) {
    if (!this.checkbox) {
      return;
    }

    this.checkbox.disabled =
      isBusy || this.hidden || this.hasAttribute("data-initializing") || Boolean(this.priceElement?.hidden);
    if (isBusy) {
      this.setAttribute("aria-busy", "true");
    } else {
      this.removeAttribute("aria-busy");
    }
  }

  setStatus(message) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.textContent = message;
    this.statusElement.hidden = message === "";
  }
}

if (!window.customElements.get("shipping-protection")) {
  window.customElements.define("shipping-protection", ShippingProtection);
}

document.addEventListener(
  "click",
  (event) => {
    if (event.target.closest?.("[data-shipping-protection-remove]")) {
      setStorageValue(false);
    }
  },
  true
);
